/**
 * @file Tooltip component
 * @description Tooltip component for displaying tooltips with custom styling. This component renders a tooltip with custom styling and supports HTML content.
 */

import React from "react";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { Tooltip as MuiTooltip, tooltipClasses } from "@mui/material";

/**
 * StyledTooltip component that applies custom styling to the MuiTooltip component.
 *
 * @param {object} props - Component props
 * @param {string} props.className - CSS class name
 * @returns {JSX.Element} - StyledTooltip component
 */
const StyledTooltip = styled(({ className, ...props }) => (
  <MuiTooltip {...props} arrow classes={{ popper: className }} />
))`
  & .${tooltipClasses.arrow} {
    color: #1366ff;
  }
  & .${tooltipClasses.tooltip} {
    font-size: 10px !important;
    color: "white";
    background-color: #1366ff;
    padding: 12px 16px;

    a,
    a:visited {
      color: white;
      text-decoration: underline;
    }
  }
`;

/**
 * Tooltip component that renders a tooltip with custom styling and HTML content.
 *
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - The content to be wrapped by the tooltip
 * @param {string} props.html - The HTML content to be displayed in the tooltip
 * @param {string} [props.placement="right"] - The placement of the tooltip (default: "right")
 * @returns {JSX.Element} - Tooltip component
 */
const Tooltip = ({ children, html, placement = "right" }) => {
  return (
    <StyledTooltip
      placement={placement}
      title={
        <React.Fragment>
          <Typography
            color="inherit"
            component="span"
            dangerouslySetInnerHTML={{ __html: html }}
          ></Typography>
        </React.Fragment>
      }
      arrow
    >
      {children}
    </StyledTooltip>
  );
};

export default Tooltip;
